import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

/**
 * Tabs across the top of the homepage's main panel. Home is always there and
 * cannot be closed; everything else is opened from the chat, worked on, and
 * closed again.
 *
 * The chat rail sits outside these tabs and never changes with them, so a DJ
 * can keep asking questions while a canvas is open.
 */

export type CanvasKind = 'duplicates' | 'table';

export type CanvasTab = {
  /** Reopening the same id focuses the existing tab instead of stacking. */
  id: string;
  kind: CanvasKind;
  title: string;
  /** Markdown for an answer popped out of the chat. */
  markdown?: string;
};

type CanvasContextValue = {
  tabs: CanvasTab[];
  /** null means Home. */
  activeId: string | null;
  openCanvas: (tab: CanvasTab) => void;
  closeCanvas: (id: string) => void;
  showHome: () => void;
};

const CanvasContext = createContext<CanvasContextValue | null>(null);

/** Null away from the homepage, so callers fall back to normal navigation. */
export const useCanvas = (): CanvasContextValue | null =>
  useContext(CanvasContext);

/**
 * True for content already rendered inside a canvas. Shared components use it
 * to stop shortening themselves for the chat rail once they have the room.
 */
const InCanvas = createContext(false);

export const InCanvasProvider = InCanvas.Provider;

export const useInCanvas = (): boolean => useContext(InCanvas);

export const CanvasProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [tabs, setTabs] = useState<CanvasTab[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const openCanvas = useCallback((tab: CanvasTab) => {
    setTabs((current) => {
      if (current.some((item) => item.id === tab.id)) return current;
      return [...current, tab];
    });
    setActiveId(tab.id);
  }, []);

  const closeCanvas = useCallback((id: string) => {
    setTabs((current) => current.filter((item) => item.id !== id));
    // Home rather than a neighbouring tab, so closing is predictable.
    setActiveId((active) => (active === id ? null : active));
  }, []);

  const showHome = useCallback(() => setActiveId(null), []);

  const value = useMemo(
    () => ({ tabs, activeId, openCanvas, closeCanvas, showHome }),
    [tabs, activeId, openCanvas, closeCanvas, showHome],
  );

  return (
    <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>
  );
};
